// Paced captions (design 7.6). The voice engine's transcription runs ahead of
// the audio, so captions reveal words at an estimated speaking pace from the
// moment Otto's sound starts, in a fixed three-line box, instead of dumping
// the whole sentence early. The pacing numbers are the booth's, which Wes saw
// at the show (tradeshow-ottos-demo src/consult/CallStage.jsx, see
// PORTED-FROM.md). Timing is an estimate, not true word timing.
import { stripDashes } from "../visibleText";

/** Estimated time one word takes to say, in ms, with a pause after punctuation. */
export function captionWordDuration(word: string): number {
  const coreLength = word.replace(/[^\p{L}\p{N}]/gu, "").length;
  const punctuationPause = /[.!?]$/.test(word) ? 170 : /[,;:]$/.test(word) ? 90 : 0;
  return Math.min(520, Math.max(170, 115 + coreLength * 25)) + punctuationPause;
}

/** When each word appears, in ms after Otto's sound starts. The first word waits 120 ms. */
export function captionOffsets(words: readonly string[]): number[] {
  let elapsed = 120;
  return words.map((word) => {
    const offset = elapsed;
    elapsed += captionWordDuration(word);
    return offset;
  });
}

export function captionWords(text: string): string[] {
  return stripDashes(text).trim().match(/\S+/g) ?? [];
}

/** How many words to show `elapsedMs` after Otto's sound started. */
export function visibleWordCount(offsets: readonly number[], elapsedMs: number): number {
  let count = 0;
  for (const offset of offsets) {
    if (offset > elapsedMs) break;
    count += 1;
  }
  return count;
}

/**
 * Accumulates one Otto turn's transcript. The server may send each chunk as a
 * delta (what the voice engine does) or as the text so far; both are handled:
 * a chunk that starts with the text so far replaces it, anything else appends.
 */
export function appendTranscript(current: string, chunk: string): string {
  if (!chunk) return current;
  if (!current) return chunk;
  if (chunk.startsWith(current)) return chunk;
  return current + chunk;
}
